(ns nl.mediquest.ui.core.search
  (:require
   [bidi.bidi :as bidi]
   [clojure.spec.alpha :as s]
   [clojure.string :as string]
   [integrant.core :as ig]
   [nl.mediquest.ui.util :as util]
   [re-frame.core :as re-frame]
   [reagent.core :as r]))

(def ^:private max-number-of-results 8)

(def ^:private similarity-threshold
  ;; fixed at index time; might need some careful tuning
  0.45) ; seems a sensible value for now

(defn- page-key->path
  [routes page-k]
  (if (coll? page-k)
    (->> page-k
         (keep (partial page-key->path routes))
         first)
    (bidi/path-for routes page-k)))

(defn- resolve-ref
  [config k]
  (-> config (ig/find-derived k) first second))

(defn- page-components
  [config page-config]
  (->> page-config
       :page/body
       :key
       (resolve-ref config)
       :body/components
       (map :key)))

(defn- section-title
  [{:section/keys [title]}]
  title)

(defn- question-title
  [component]
  (when-let [spec (:spec component)]
    (let [[_id schema] spec
          [type config] schema
          config (if (= :maybe type)
                   (second config)
                   config)]
      (:question/title config))))

(defn- component-config->title
  [component]
  (or (section-title component)
      (question-title component)))

(defn- page-title
  [page-config]
  (or (:page/title-in-search-result page-config)
      (:page/title page-config)))

(defn- configs->items
  [configs]
  (for [{:keys [config routes-k base-url]} configs
        :let [routes ["/" (resolve-ref config routes-k)]]
        [page-k page-config] (ig/find-derived config :component.ui/page)
        component-k (page-components config page-config)
        :let [component-config (resolve-ref config component-k)
              component-title (component-config->title component-config)]
        :when component-title]
    {:page-title (page-title page-config)
     :base-url base-url
     :path (page-key->path routes page-k)
     :anchor (util/component-key->anchor-id component-k)
     :component-title component-title}))

(defn configs->index
  "The Fuse class needs to be a parameter, because we don't want all
   projects that depend on mq-ui to be forced to list it as a dependency
   (or having `mq-ui` in `checkouts` would break)."
  [Fuse configs]
  (let [items (configs->items configs)]
    (Fuse. (clj->js items)
           (clj->js {"keys" ["component-title"]
                     "threshold" similarity-threshold}))))

(re-frame/reg-event-db
 ::got-index-data
 (fn [db [_ index]]
   (assoc db ::search-index index)))

(re-frame/reg-sub
 ::search-index
 (fn [db]
   (::search-index db)))

(defonce ^:private search-dialog-open?
  (r/atom false))

(defn- parse-namespaced-keyword [s]
  (let [[namespace name] (string/split s #"/")]
    (keyword namespace name)))

(def ^:private
  missing-substitution-mark
  "[MISSING-SUBSTITUTION]")

(defn- replacement
  [opts placeholder-match]
  (let [substitution
        (-> placeholder-match second parse-namespaced-keyword opts)]
    (if (string/blank? substitution)
      missing-substitution-mark
      substitution)))

(defn- replace-base-url-placeholders
  "Substitutes a placeholder in a base-url (if any) from opts, or returns nil
   in case no valid substitution is given for the placeholder (likely running
   locally)."
  [opts base-url]
  (when-not (string/blank? base-url)
    (let [result (string/replace base-url #"\{\{:([^}]+)\}\}" #(replacement opts %))]
      (when-not (string/includes? result missing-substitution-mark)
        result))))

(defn- make-link
  [opts base-url path anchor]
  (-> (replace-base-url-placeholders opts base-url)
      (str path "#" anchor)))

(defn- render-result
  [opts {:strs [page-title base-url path anchor component-title] :as _result}]
  [:div
   [:a
    {:href (make-link opts base-url path anchor)
     :on-click #(reset! search-dialog-open? false)}
    [:div.text-primary.text-sm page-title
     [:div.pl-8.pb-2.text-sm.text-black component-title]]]])

(defn- render-results
  [opts search-index query]
  [:div.mt-2
   {:style {:min-height "12rem"}}
   (when-not (string/blank? query)
     (let [results
           (->> (.search search-index (string/trim query))
                (take max-number-of-results)
                js->clj
                (map #(get % "item")))]
       (doall
        (map-indexed (fn [i result]
                       ^{:key i}
                       [render-result opts result])
                     results))))])

(defn- search-dialog
  [opts search-index]
  (let [query (r/atom "")]
    (fn []
      [:div#search-dialog.absolute.shadow-lg.right-4.-top-4.bg-white.p-4.pb-2.print:hidden
       [:input.px-4.py-2.mr-16.text-sm.rounded-sm.bg-gray-200.text-gray-700.outline-none
        {:type "text"
         :placeholder "Zoek specifieke items"
         :value @query
         :autoComplete "off"
         :autoFocus true
         :on-change (fn [event]
                      (->> event .-target .-value (reset! query)))}]
       [render-results opts search-index @query]])))

(defn- close-search-dialog
  [event]
  (when-not (.. js/document
                (getElementById "search-dialog")
                (contains (.-target event)))
    (reset! search-dialog-open? false)))

(defn- closing-search-dialog
  [opts search-index]
  (when @search-dialog-open?
    (r/with-let [_ (js/document.addEventListener "click" close-search-dialog)]
      [search-dialog opts search-index]
      (finally
        (js/document.removeEventListener "click" close-search-dialog)))))

(defn- search-widget
  [{:search/keys [icon]
    :or {icon "fa-magnifying-glass"}
    :as opts}]
  (let [search-index @(re-frame/subscribe [::search-index])]
    (when search-index
      [:div.flex.items-center.hidden.md:block
       [:i.text-primary.p-4.cursor-pointer.text-3xl
        {:class (str icon " " (if @search-dialog-open? "fas" "far"))
         :on-click #(swap! search-dialog-open? not)}]
       [:div.relative.print:hidden ; print:hidden does not seem to work at all in Brave?!?
        [closing-search-dialog opts search-index]]])))

(defn- render
  [opts]
  (re-frame/dispatch [::prepare-index])
  [search-widget opts])

(s/def :search/dashboard-url string?)
(s/def :search/icon string?)

(defmethod ig/assert-key :component.ui/search [_k v]
  (assert (s/valid?
           (s/keys :opt [:search/dashboard-url ; might add options for other application's URLs when relevant
                         :search/icon])
           v)))

(defmethod ig/init-key :component.ui/search
  [_ opts]
  (fn []
    [render opts]))

;; use this effect to provide config(s) to the search function
(re-frame/reg-fx
 ::build-index
 (fn [{:keys [Fuse configs]}]
   (re-frame/dispatch [::got-index-data (configs->index Fuse configs)])))
